import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';


const ContentSection = styled.div`
  padding: 120px 0 60px;
  min-height: calc(100vh - 300px);
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0 ${theme.spacing.md};
  }
`;


const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
  color: ${theme.colors.primary.dark};
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${theme.colors.neutral.grey};
  font-size: ${theme.typography.sizes.sm};
  margin-bottom: ${theme.spacing['2xl']};
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.xl};
`;

const Content = styled.div`
  line-height: 1.8;
  color: ${theme.colors.neutral.darkGrey};
  
  p {
    margin-bottom: ${theme.spacing.lg};
  }
  
  ul {
    margin-bottom: ${theme.spacing.lg};
    padding-left: ${theme.spacing.xl};
    
    li {
      margin-bottom: ${theme.spacing.sm};
    }
  }
  
  strong {
    font-weight: ${theme.typography.fontWeights.semibold};
    color: ${theme.colors.primary.dark};
  }
`;

const ImportantBox = styled.div`
  background: ${theme.colors.primary.beige}20;
  border: 2px solid ${theme.colors.primary.blue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.xl} 0;
  
  p {
    margin: 0;
    font-weight: ${theme.typography.fontWeights.medium};
  }
`;

export const Terms: React.FC = () => {
  return (
    <SharedLayoutUltimate>
      <ContentSection>
        <Container>
          <PageTitle>Terms of Service</PageTitle>
          <LastUpdated>Last Updated: October 8, 2025</LastUpdated>
          
          <Content>
            <ImportantBox>
              <p>
                <strong>IMPORTANT:</strong> This website provides educational information only. It does not provide investment advice, 
                financial planning services, or recommendations to purchase any specific product.
              </p>
            </ImportantBox>

            <Section>
              <SectionTitle>Acceptance of Terms</SectionTitle>
              <p>
                By using AnnuityAnswers.com, you agree to these Terms of Service. If you do not agree, please do not use this website.
              </p>
            </Section>

            <Section>
              <SectionTitle>Educational Purpose Only</SectionTitle>
              <p>
                This website and its tools are provided for educational and informational purposes only. The information presented:
              </p>
              <ul>
                <li>Is not personalized investment advice</li>
                <li>Is not a recommendation to buy or sell any annuity product</li>
                <li>Should not be the sole basis for any financial decision</li>
                <li>May not be suitable for your specific situation</li>
              </ul>
              <p>
                <strong>Always consult with qualified financial advisors, tax professionals, and legal counsel before making any investment decisions.</strong>
              </p>
            </Section>

            <Section>
              <SectionTitle>No Guarantees or Predictions</SectionTitle>
              <p>
                We make no guarantees about future performance. Specifically:
              </p>
              <ul>
                <li>Past performance does not guarantee future results</li>
                <li>Historical data and projections are not predictions of future performance</li>
                <li>Actual results will vary from any illustrations or projections</li>
                <li>All investments involve risk, including potential loss of principal</li>
              </ul>
            </Section>

            <Section>
              <SectionTitle>Regulatory Compliance</SectionTitle>
              <p>
                Our tools and methodologies distinguish between actual historical data and hypothetical performance. We comply with applicable insurance 
                regulations, including NAIC Model Regulation standards. Any hypothetical or backtested data is clearly labeled.
              </p>
              <p>
                <strong>Rate Drift Methodology:</strong> Our proprietary "Rate Drift" concept accounts for potential rate changes over time. 
                This is an educational model and not a guarantee of how rates will actually change.
              </p>
            </Section>

            <Section>
              <SectionTitle>Limitation of Liability</SectionTitle>
              <p>
                <strong>To the maximum extent permitted by law:</strong>
              </p>
              <ul>
                <li>We are not liable for any investment losses or damages</li>
                <li>We are not responsible for decisions you make based on this website</li>
                <li>We do not guarantee the accuracy, completeness, or timeliness of information</li>
                <li>Our total liability is limited to the amount you paid to use this service (which is $0)</li>
              </ul>
            </Section>

            <Section>
              <SectionTitle>No Professional Relationship</SectionTitle>
              <p>
                Using this website does not create:
              </p>
              <ul>
                <li>A client relationship</li>
                <li>A fiduciary relationship</li>
                <li>An advisory agreement</li>
                <li>Any obligation for us to provide personalized advice</li>
              </ul>
            </Section>

            <Section>
              <SectionTitle>Intellectual Property</SectionTitle>
              <p>
                All content, methodologies, and proprietary concepts (including "Rate Drift" and "Date Lottery") are owned by 
                Cap Averill II & Associates. You may not reproduce, distribute, or create derivative works without written permission.
              </p>
            </Section>

            <Section>
              <SectionTitle>User Responsibilities</SectionTitle>
              <p>
                By using this website, you agree to:
              </p>
              <ul>
                <li>Provide accurate information if you choose to use our forms</li>
                <li>Use the website only for lawful purposes</li>
                <li>Not attempt to access restricted areas or interfere with the website</li>
                <li>Independently verify any information before making financial decisions</li>
              </ul>
            </Section>

            <Section>
              <SectionTitle>State Regulations</SectionTitle>
              <p>
                Insurance products and regulations vary by state. Information on this website may not be applicable in all states. 
                Check with your state's insurance department and licensed professionals in your state.
              </p>
            </Section>

            <Section>
              <SectionTitle>Changes to Terms</SectionTitle>
              <p>
                We may update these Terms of Service at any time. Continued use of the website after changes constitutes acceptance of the new terms.
              </p>
            </Section>

            <Section>
              <SectionTitle>Dispute Resolution</SectionTitle>
              <p>
                Any disputes will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. 
                This agreement is governed by the laws of the state where Cap Averill II & Associates is headquartered.
              </p>
            </Section>

            <Section>
              <SectionTitle>Contact Information</SectionTitle>
              <p>
                For questions about these Terms of Service:
              </p>
              <p>
                Cap Averill II & Associates<br />
                Phone: 800-922-1894
              </p>
            </Section>
          </Content>
        </Container>
      </ContentSection>
    </SharedLayoutUltimate>
  );
};